import * as Dialog from '@wandb/weave/components/Dialog';
import * as Tooltip from '@wandb/weave/components/RadixTooltip';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {type FC, useEffect, useMemo, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {twMerge} from 'tailwind-merge';

import {useProjectParams} from '../../pages/ProjectPage/useProjectParams';
import {RUN_PATHS} from '../../routes/workspace/singleRunWorkspacePaths';
import {useMemoizedSelector} from '../../state/hooks';
import {type LoadableView} from '../../state/views/types';
import {WORKSPACE_PLACEHOLDER_NAME} from '../../state/workspaces/displayNames';
import {
  getDataChecked,
  getDataMaybe,
  getStatus,
  makeWorkspaceViewSelector,
} from '../../state/workspaces/selectors';
import {namedWorkspaceBelongsToUser} from '../../state/workspaces/types';
import {workspaceViewListSelector} from '../../state/workspaces/workspaceViewListSelector';
import {DD_PRIVACY_ALLOW} from '../../util/datadog/privacy';
import {setDocumentTitle} from '../../util/document';
import {SemanticLoader as Loader} from '../utility/InstrumentedLoader';
import {IngestionDelayedPill} from './components/IngestionDelayedPill';
import {NamedWorkspaceModalContent} from './components/modals/NamedWorkspaceModalContent';
import {AutoSaveWarning} from './components/NamedWorkspaceAutoSaveWarning';
import {NamedWorkspaceDisplayName} from './components/NamedWorkspaceDisplayName';
import {NamedWorkspaceMenu} from './components/NamedWorkspaceMenu';
import {NamedWorkspaceOverflowMenu} from './components/NamedWorkspaceOverflowMenu';
import {
  NamedWorkspaceTimestamp,
  NamedWorkspaceUndoRedo,
} from './components/NamedWorkspaceUndoRedo';
import {
  NamedWorkspaceTypePills,
  PanelGenerationIconPill,
} from './components/PillWithTooltip';
import {ServiceDegradedPill} from './components/ServiceDegradedPill';
import {useViewBarKeyboardShortcuts} from './components/useViewBarKeyboardShortcuts';
import {ViewBarButton} from './components/ViewBarButton';
import {
  useViewBarActionContext,
  useViewBarContext,
  ViewBarContextProvider,
  type ViewBarContextType,
} from './NamedWorkspaceViewBarContext';
import {type NamedWorkspaceViewBarProps} from './types';
import {parseAndTrackNamedWorkspaces} from './utils';

export const NamedWorkspaceViewBarWrap: FC<NamedWorkspaceViewBarProps> = ({
  workspaceStateId,
  viewType,
  isProjectReadOnly,
}) => {
  const viewState = useMemoizedSelector(
    makeWorkspaceViewSelector,
    workspaceStateId
  );
  const workspaceState = useMemoizedSelector(
    workspaceViewListSelector,
    workspaceStateId
  );

  if (
    getStatus(viewState) === 'loading' ||
    getStatus(workspaceState) === 'loading'
  ) {
    return <Loader name="named-workspace-view-bar-loader" />;
  }
  const readyWorkspaceState = getDataMaybe(workspaceState);

  if (
    getStatus(viewState) !== 'ready' ||
    getStatus(workspaceState) !== 'ready' ||
    readyWorkspaceState == null ||
    readyWorkspaceState.sharedViews == null ||
    readyWorkspaceState.personalWorkspaces == null ||
    readyWorkspaceState.workspace == null
  ) {
    console.error(
      'Could not load workspace because viewer or workspace information not found.'
    );
    return <NamedWorkspaceViewBarError />;
  }

  if (readyWorkspaceState.workspace.nwId == null) {
    return <Loader name="named-workspace-view-bar-loader" />;
  }

  return (
    <ViewBarContextProvider
      workspaceStateId={workspaceStateId}
      sharedViews={readyWorkspaceState.sharedViews}
      personalWorkspaces={readyWorkspaceState.personalWorkspaces}
      currentWorkspaceNwId={readyWorkspaceState.workspace.nwId}
      currentWorkspaceViewRef={readyWorkspaceState.workspace.viewRef}
      currentWorkspaceType={readyWorkspaceState.workspace.viewType}
      currentView={getDataChecked(viewState).view}
      viewType={viewType}
      isProjectReadOnly={isProjectReadOnly}>
      <NamedWorkspaceViewBarComp />
    </ViewBarContextProvider>
  );
};

const NamedWorkspaceViewBarError = () => {
  return (
    <Tailwind>
      <div className="bg-red-300/[0.24] p-8">
        <p>
          There was a problem with workspaces. Please contact support@wandb.com
          for help.
        </p>
      </div>
    </Tailwind>
  );
};

const NamedWorkspaceViewBarComp = () => {
  const {
    displayName,
    currentWorkspaceNwId,
    currentView,
    isModalOpen,
    viewer,
    isProjectReadOnly,
    renameWorkspace,
    hasUnsavedChanges,
    copyToPersonalWorkspace,
  } = useViewBarContext();
  const {setModalDetails, setIsModalOpen} = useViewBarActionContext();

  useWorkspaceTabTitle();
  useViewBarKeyboardShortcuts();

  useEffect(() => {
    parseAndTrackNamedWorkspaces({
      action: 'View',
      location: 'page',
      currentView,
      viewer,
    });
  }, [currentView, currentWorkspaceNwId.nwType, viewer]);

  const [isRenaming, setIsRenaming] = useState(false);

  const visibleActionFlags = useMemo(
    () =>
      determineVisibleActionFlags({
        renameWorkspace,
        copyToPersonalWorkspace,
        isRenaming,
      }),
    [copyToPersonalWorkspace, isRenaming, renameWorkspace]
  );

  const onDialogOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setModalDetails(undefined);
    }
  };

  const isUnsaved = viewer?.username
    ? hasUnsavedChanges &&
      !namedWorkspaceBelongsToUser(currentWorkspaceNwId, viewer)
    : true;

  return (
    <TailwindContents>
      <Dialog.Root open={isModalOpen} onOpenChange={onDialogOpenChange}>
        <Tooltip.Provider>
          <NamedWorkspaceViewBarContent
            {...visibleActionFlags}
            displayName={displayName}
            isProjectReadOnly={isProjectReadOnly}
            currentView={currentView}
            isUnsaved={isUnsaved}
            isRenaming={isRenaming}
            setIsRenaming={setIsRenaming}
          />
          <NamedWorkspaceModalContent />
        </Tooltip.Provider>
      </Dialog.Root>
    </TailwindContents>
  );
};

function useWorkspaceTabTitle() {
  const {displayName} = useViewBarContext();
  const {projectName} = useProjectParams();
  const isInRunDetails = useRouteMatch(RUN_PATHS) != null;
  useEffect(() => {
    if (isInRunDetails) {
      return;
    }
    const hasNamedWorkspace =
      displayName && displayName !== WORKSPACE_PLACEHOLDER_NAME;

    setDocumentTitle(
      hasNamedWorkspace ? `${displayName} | ${projectName}` : projectName
    );
  }, [displayName, projectName, isInRunDetails]);
}

export function determineVisibleActionFlags({
  renameWorkspace,
  copyToPersonalWorkspace,
  isRenaming,
}: Partial<ViewBarContextType> & {isRenaming: boolean}) {
  return {
    isRenameAllowed: renameWorkspace != null && !isRenaming,
    isPrimaryButtonVisible: copyToPersonalWorkspace != null,
  };
}

export const NamedWorkspaceViewBarContent = ({
  currentView,
  displayName,
  isRenameAllowed,
  isProjectReadOnly,
  isUnsaved,
  isPrimaryButtonVisible,
  isRenaming,
  setIsRenaming,
}: NwLeftAlignedContentProps & NwRightAlignedContentProps) => {
  return (
    <div
      data-test="nw-view-bar-content"
      data-dd-privacy={DD_PRIVACY_ALLOW}
      className={twMerge(
        'flex flex-wrap items-center',
        'gap-4 px-16 py-12',
        'border-b border-wb-border bg-wb-surface-sunken'
      )}>
      <NwLeftAlignedContent
        displayName={displayName}
        isRenameAllowed={isRenameAllowed}
        setIsRenaming={setIsRenaming}
        isRenaming={isRenaming}
        currentView={currentView}
      />
      <div
        className={twMerge(
          'ml-12 flex items-center justify-end gap-8',
          'grow md:shrink'
        )}>
        <NwRightAlignedContent
          isProjectReadOnly={isProjectReadOnly}
          isUnsaved={isUnsaved}
          isPrimaryButtonVisible={isPrimaryButtonVisible}
        />
      </div>
    </div>
  );
};

type NwLeftAlignedContentProps = {
  currentView: LoadableView | undefined;
  displayName: string;
  isRenameAllowed: boolean;
  isRenaming: boolean;
  setIsRenaming: (isRenaming: boolean) => void;
};
const NwLeftAlignedContent = (props: NwLeftAlignedContentProps) => {
  return (
    <>
      <div
        className={twMerge(
          'flex items-center gap-4',
          '2xl:max-width-none max-w-[50%] md:max-w-[45%]'
        )}>
        <NamedWorkspaceMenu />
        <NamedWorkspaceDisplayName {...props} />
        {!props.isRenaming && <NamedWorkspaceTypePills />}
      </div>
      {!props.isRenaming && <PanelGenerationIconPill />}
    </>
  );
};

type NwRightAlignedContentProps = Pick<
  ViewBarContextType,
  'isProjectReadOnly'
> & {
  isUnsaved: boolean;
  isPrimaryButtonVisible: boolean;
};
const NwRightAlignedContent = ({
  isProjectReadOnly,
  isUnsaved,
  isPrimaryButtonVisible,
}: NwRightAlignedContentProps) => {
  return (
    <>
      <ServiceDegradedPill />
      <IngestionDelayedPill />
      {isUnsaved && <AutoSaveWarning isProjectReadOnly={isProjectReadOnly} />}
      {!isUnsaved && (
        <NamedWorkspaceTimestamp className="hidden md:contents md:whitespace-nowrap" />
      )}
      <NamedWorkspaceUndoRedo />
      <NamedWorkspaceOverflowMenu />
      {isPrimaryButtonVisible && <ViewBarButton />}
    </>
  );
};
